import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, KeyRound, LifeBuoy, LogOut, ShieldCheck, Fingerprint, BadgeCheck, Building2, Landmark, FileText } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/pages/Services";
import { Screen, Card, Btn, Input, Chip } from "./ui";
import { visibleServices, SERVICES, GROUPS } from "./services";
import { ServiceTile } from "./Tile";
import { clearLock, getLock, isIOS } from "./lock";

export function MServices() {
  const { user } = useAuth();
  const meta = useMeta();
  if (!meta) return <Screen title="Services" back={false}><div className="py-10 text-center text-sm text-slate-400">Loading…</div></Screen>;
  const list = visibleServices(user, meta);
  return (
    <Screen title="Services" back={false} testid="m-services">
      <div className="space-y-4">
        {GROUPS.map(([g, label]) => {
          const items = list.filter((x) => x.group === g);
          if (!items.length) return null;
          return (
            <Card key={g} data-testid={`m-svc-group-${g}`}>
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
              <div className="grid grid-cols-4 gap-y-5">{items.map((x) => <ServiceTile key={x.key} s={x} />)}</div>
            </Card>
          );
        })}
      </div>
      {!meta.live_services && <p className="mt-4 text-xs text-slate-500" data-testid="m-svc-note">“SOON” wali services portal par live hote hi yahan chalne lagengi.</p>}
      {isIOS() && <p className="mt-2 text-xs text-slate-500">AEPS and Aadhaar Pay need a fingerprint device and are available on Android only.</p>}
    </Screen>
  );
}

export function MServiceEmbed() {
  const { key } = useParams();
  const { user } = useAuth();
  const meta = useMeta();
  const s = SERVICES.find((x) => x.key === key);
  if (!meta) return null;
  const v = visibleServices(user, meta).find((x) => x.key === key);
  if (!s || !s.el || !v || v.soon) return <Navigate to="/app/services" replace />;
  if (s.native) return s.el;
  return <Screen title={s.label} testid={`m-svc-page-${key}`}><div className="m-embed">{s.el}</div></Screen>;
}

function ChangeForm({ url, keys, label, numeric, testid }) {
  const [v, setV] = useState({ a: "", b: "", c: "" });
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    if (v.b !== v.c) return toast.error(`New ${label} does not match`);
    setBusy(true);
    try { await api.post(url, { [keys[0]]: v.a, [keys[1]]: v.b }); toast.success(`${label} changed`); setV({ a: "", b: "", c: "" }); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const p = numeric ? { inputMode: "numeric", maxLength: 4 } : {};
  return (
    <form onSubmit={go} className="space-y-3" data-testid={testid}>
      <Input label={`Old ${label}`} type="password" {...p} value={v.a} onChange={(e) => setV({ ...v, a: e.target.value })} required data-testid={`${testid}-old`} />
      <Input label={`New ${label}`} type="password" {...p} value={v.b} onChange={(e) => setV({ ...v, b: e.target.value })} required data-testid={`${testid}-new`} />
      <Input label={`Confirm ${label}`} type="password" {...p} value={v.c} onChange={(e) => setV({ ...v, c: e.target.value })} required data-testid={`${testid}-confirm`} />
      <Btn type="submit" loading={busy} data-testid={`${testid}-submit`}>Change {label}</Btn>
    </form>
  );
}

function Row({ icon: Icon, label, onClick, to, testid, right, danger }) {
  const inner = <><Icon className={`h-5 w-5 ${danger ? "text-[#F26B1D]" : "text-[#0F2C47]"}`} /><span className={`flex-1 text-sm font-medium ${danger ? "text-[#F26B1D]" : ""}`}>{label}</span>{right || <ChevronRight className="h-5 w-5 text-slate-400" />}</>;
  const cls = "flex w-full items-center gap-3 border-b border-slate-100 py-4 text-left last:border-0";
  return to ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link> : <button onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
}

function BankAccounts() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/settlement-accounts").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="pb-3 text-xs text-slate-400">Loading…</div>;
  if (!rows.length) return <div className="pb-3 text-xs text-slate-500">No saved bank account. Add one on the web portal.</div>;
  return <div className="space-y-2 pb-3">{rows.map((a) => <div key={a.id} className="rounded-xl bg-slate-100 p-3 text-xs"><b className="text-sm">{a.bank}</b><div>{a.holder_name} · <span className="font-mono">{a.account_no}</span> · {a.ifsc}</div><div className="mt-1"><Chip s={a.status || "APPROVED"} /></div></div>)}</div>;
}

export function MProfile({ onRelock }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState("");
  const lock = getLock(user.id);
  const out = async () => { clearLock(user.id); await logout(); };
  const tog = (k) => setOpen(open === k ? "" : k);
  return (
    <Screen title="Profile" back={false} testid="m-profile">
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4">
        <div className="grid h-14 w-14 place-items-center rounded-full bg-slate-100 font-display text-xl font-bold text-[#0F2C47]">{user.name[0]}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 truncate font-display text-lg font-bold">{user.name}{user.kyc_status === "APPROVED" && <BadgeCheck className="h-5 w-5 text-sky-600" data-testid="m-verified" />}</div>
          <div className="text-xs text-slate-500">Retailer</div>
          <div className="text-xs text-slate-500">Merchant ID: <span className="font-mono">{user.login_id}</span></div>
        </div>
        <Chip s={user.kyc_status} testid="m-kyc-status" />
      </div>
      <div className="mt-4">
        <Row icon={Building2} label="Business Details" onClick={() => tog("biz")} testid="m-row-business" />
        {open === "biz" && <div className="space-y-1 pb-3 text-sm text-slate-600" data-testid="m-shop"><div><b>Shop:</b> {user.shop_name || "-"}</div><div><b>Mobile:</b> {user.mobile}</div><div><b>Address:</b> {[user.address, user.city, user.state, user.pincode].filter(Boolean).join(", ") || "-"}</div></div>}
        <Row icon={Landmark} label="Bank Accounts" onClick={() => tog("bank")} testid="m-row-banks" />
        {open === "bank" && <BankAccounts />}
        <Row icon={ShieldCheck} label="Security & Privacy" onClick={() => tog("sec")} testid="m-row-security" />
        {open === "sec" && (
          <div className="space-y-2 pb-3">
            <button onClick={() => tog("pw")} className="w-full rounded-xl bg-slate-100 p-3 text-left text-sm font-semibold" data-testid="m-row-password"><KeyRound className="mr-2 inline h-4 w-4" />Change password</button>
            <button onClick={() => tog("mpin")} className="w-full rounded-xl bg-slate-100 p-3 text-left text-sm font-semibold" data-testid="m-row-mpin"><ShieldCheck className="mr-2 inline h-4 w-4" />Change MPIN</button>
            <button onClick={() => { if (window.confirm("Set up app lock again?")) { clearLock(user.id); onRelock(); } }} className="w-full rounded-xl bg-slate-100 p-3 text-left text-sm font-semibold" data-testid="m-row-applock"><Fingerprint className="mr-2 inline h-4 w-4" />App lock · {lock?.bio ? "Fingerprint + PIN" : "PIN"}</button>
          </div>
        )}
        {open === "pw" && <div className="pb-4"><ChangeForm url="/auth/change-password" keys={["old_password", "new_password"]} label="password" testid="m-change-password" /></div>}
        {open === "mpin" && <div className="pb-4"><ChangeForm url="/auth/change-mpin" keys={["old_mpin", "new_mpin"]} label="MPIN" numeric testid="m-change-mpin" /></div>}
        <Row icon={LifeBuoy} label="Help & Support" to="/app/support" testid="m-row-support" />
        <Row icon={FileText} label="Terms & Conditions" onClick={() => tog("terms")} testid="m-row-terms" />
        {open === "terms" && <div className="pb-3 text-xs leading-relaxed text-slate-500">All transactions are subject to bank and service-provider rules. Keep your password, MPIN and app PIN secret; never share OTPs. Transactions once processed cannot be reversed from the app — contact support for disputes.</div>}
        <div className="mt-4"><Row icon={LogOut} label="Logout" onClick={out} testid="m-row-logout" danger /></div>
      </div>
    </Screen>
  );
}
